import { yupResolver } from "@hookform/resolvers/yup";
import { useCourseApplicationForm } from "api/course-forms/useCourseApplicationForm";
import { useCoursesOptions } from "api/hooks/useCourses";
import { useAdminRegisterUserToCourse } from "api/student-courses";
import { PaziaButton, PaziaCheckbox, PaziaDateSelect, PaziaInput, PaziaSelect } from "components/atoms";
import { PaziaModal } from "components/molecules";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { COURSE_FORM_FIELD_TYPES, COURSE_FORM_FIELD_TYPES__DATES_TYPES } from "consts";
import React from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

interface AddStudentToCourseModalProps {
  studentId: number;
  isOpen: boolean;
  onCloseRequest: () => void;
  onSuccess: () => void;
}

interface FormValues {
  courseId: string;
  fields: { id: number; value: string }[];
  isInvoice: boolean;
  buyer: string;
  buyerNip: string;
  buyerAddress: string;
}

const schema = yup.object().shape({
  courseId: yup.string().required("Wybierz kurs"),
  fields: yup.array().of(
    yup.object().shape({
      id: yup.number().required(),
      value: yup.string(),
    }),
  ),
  isInvoice: yup.boolean(),
  buyer: yup.string(),
  buyerNip: yup.string(),
  buyerAddress: yup.string(),
});

const AddStudentToCourseContent = ({
  studentId,
  onCloseRequest,
  onSuccess,
}: {
  studentId: number;
  onCloseRequest: () => void;
  onSuccess: () => void;
}) => {
  const { options: courseOptions, isLoading: coursesLoading } = useCoursesOptions("", "");
  const registerMutation = useAdminRegisterUserToCourse(studentId);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      courseId: "",
      fields: [],
      isInvoice: false,
      buyer: "",
      buyerNip: "",
      buyerAddress: "",
    },
  });

  const selectedCourseId = useWatch({ control: methods.control, name: "courseId" });
  const isInvoice = useWatch({ control: methods.control, name: "isInvoice" });

  const { loading: formLoading, data: applicationForm } = useCourseApplicationForm(
    selectedCourseId ? Number(selectedCourseId) : undefined,
  );

  // Reset fields when course changes
  React.useEffect(() => {
    if (!applicationForm?.groups) return;
    const allFields: { id: number; value: string }[] = [];
    applicationForm.groups.forEach((group) => {
      group.fields.forEach((field) => {
        allFields.push({ id: field.id, value: "" });
      });
    });
    methods.setValue("fields", allFields);
  }, [applicationForm]);

  const onSubmit = async (data: FormValues) => {
    try {
      await registerMutation.mutateAsync({
        courseId: Number(data.courseId),
        fields: data.fields.map((f) => ({ id: f.id, value: f.value })),
        buyerFields: {
          isInvoice: data.isInvoice,
          buyer: data.buyer || undefined,
          buyerNip: data.buyerNip || undefined,
          buyerAddress: data.buyerAddress || undefined,
        },
        urzadPracyFields: null,
      });
      toast.success("Kursant został dodany do kursu");
      onSuccess();
      onCloseRequest();
    } catch {
      toast.error("Wystąpił błąd podczas dodawania do kursu");
    }
  };

  const renderFieldInput = (fieldType: number, fieldIndex: number) => {
    if (COURSE_FORM_FIELD_TYPES__DATES_TYPES.includes(fieldType)) {
      return <PaziaDateSelect name={`fields.${fieldIndex}.value`} range={[new Date(1990, 1, 1), new Date()]} />;
    }

    if (fieldType === COURSE_FORM_FIELD_TYPES.BOOLEAN) {
      return (
        <select
          {...methods.register(`fields.${fieldIndex}.value`)}
          className="mt-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 sm:text-sm"
        >
          <option value="">Wybierz</option>
          <option value="true">Tak</option>
          <option value="false">Nie</option>
        </select>
      );
    }

    return <PaziaInput name={`fields.${fieldIndex}.value`} />;
  };

  let globalFieldIndex = 0;

  return (
    <FormProvider {...methods}>
      <ModalPadding form onSubmit={methods.handleSubmit(onSubmit)}>
        <ModalHeader>Dodaj kursanta do kursu</ModalHeader>

        <div className="space-y-6">
          <PaziaSelect
            name="courseId"
            label="Kurs"
            required
            options={courseOptions ?? []}
            isLoading={coursesLoading}
            placeholder="Wybierz kurs..."
          />

          {selectedCourseId && (
            <>
              {formLoading ? (
                <p className="text-sm text-slate-500">Ładowanie formularza...</p>
              ) : (
                applicationForm?.groups && (
                  <div className="space-y-6">
                    {applicationForm.groups.map((group) => (
                      <div key={group.courseFormGroupId} className="space-y-4">
                        {group.name && (
                          <h3 className="border-b border-slate-200 pb-2 text-base font-medium text-slate-900 dark:border-slate-700 dark:text-slate-100">
                            {group.name}
                          </h3>
                        )}
                        {group.description && (
                          <p className="text-sm text-slate-600 dark:text-slate-400">{group.description}</p>
                        )}
                        <div className="space-y-4">
                          {group.fields.map((field) => {
                            const idx = globalFieldIndex++;
                            return (
                              <div key={field.id}>
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                                  {field.name}
                                  {field.isRequired && <span className="ml-1 text-red-500">*</span>}
                                </label>
                                {renderFieldInput(field.fieldType, idx)}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              <div className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-700">
                <h3 className="text-base font-medium text-slate-900 dark:text-slate-100">Dane nabywcy</h3>
                <PaziaCheckbox name="isInvoice" label="Faktura VAT" />
                {isInvoice && (
                  <div className="space-y-3">
                    <PaziaInput name="buyer" label="Nabywca" />
                    <PaziaInput name="buyerAddress" label="Adres nabywcy" />
                    <PaziaInput name="buyerNip" label="NIP" />
                  </div>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end space-x-3 border-t border-slate-200 pt-6 dark:border-slate-700">
            <PaziaButton type="secondary" onClick={onCloseRequest} disabled={registerMutation.isPending}>
              Anuluj
            </PaziaButton>
            <PaziaButton
              type="primary"
              btnType="submit"
              disabled={!selectedCourseId || registerMutation.isPending}
              processing={registerMutation.isPending}
            >
              Dodaj do kursu
            </PaziaButton>
          </div>
        </div>
      </ModalPadding>
    </FormProvider>
  );
};

export const AddStudentToCourseModal = ({
  studentId,
  isOpen,
  onCloseRequest,
  onSuccess,
}: AddStudentToCourseModalProps) => {
  return (
    <PaziaModal id="add-student-to-course-modal" isOpen={isOpen} onCloseRequest={onCloseRequest} size="full">
      {isOpen && (
        <AddStudentToCourseContent studentId={studentId} onCloseRequest={onCloseRequest} onSuccess={onSuccess} />
      )}
    </PaziaModal>
  );
};
